import { lazy, Suspense, useEffect } from 'react';
import type { JSX } from 'react';

import { CommandPalette } from './components/command-palette';
import { AnalyticsPage } from './analytics/AnalyticsPage';
import { browserSsoStorage, loadSsoSession } from './auth/sso';
import { loadNotificationPrefs } from './calendar/notificationPrefs';
import { startReminderLoop } from './calendar/reminders';
import { DashboardPage } from './dashboard/DashboardPage';
import { LandingPage } from './landing/LandingPage';
import { browserStorage } from './room/devicePrefs';
import { RecordingsPage } from './recordings/RecordingsPage';
import { SettingsPage } from './settings/SettingsPage';
import { usePathname } from './lib/spa';
import { RoomPage } from './room/RoomPage';
import { roomSlugFromPath } from './room/roomState';
import { WebinarsPage } from './webinars/WebinarsPage';
import { WebinarDetailPage } from './webinars/WebinarDetailPage';
import { parseWebinarDetailPath } from './api/webinars';
import { i18n } from './i18n';

// The calendar page is a lazy chunk: FullCalendar and its view plugins stay out of the main
// bundle, which the dashboard, room and recordings paths never need.
const CalendarPage = lazy(async () => {
  const module = await import('./calendar/CalendarPage');
  return { default: module.CalendarPage };
});

/** Client-side redirect: rewrites the URL to the target and re-renders through popstate. */
function Redirect({ to, search }: { readonly to: string; readonly search: string }): JSX.Element {
  useEffect(() => {
    window.history.replaceState({}, '', to + search);
    // Deferred by a task: the pathname hook of the app shell subscribes in its own mount
    // effect, which runs after this child effect and would otherwise miss the event.
    const task = window.setTimeout(() => {
      window.dispatchEvent(new PopStateEvent('popstate'));
    }, 0);
    return () => {
      window.clearTimeout(task);
    };
  }, [to, search]);
  return <div className="min-h-screen bg-background" />;
}

/**
 * App shell and route table. `/` is the workspace dashboard (the former landing join form is
 * folded into it); `/welcome` is the Syncra landing (the dashboard sidebar's brand lockup
 * leads there; its CTAs deep-link back into the dashboard's join and quick-session flows);
 * `/calendar` is the full calendar page (lazy chunk, SSO-gated); the retired `/dashboard`
 * path answers with a client-side redirect to `/` so bookmarks keep working; `/recordings`,
 * `/webinars` (the CR-24 webinar catalog), `/analytics` (the CR-25 platform analytics) and
 * `/settings` are the archive, webinar, analytics and preference screens; `/r/:room` is the
 * call. Any other path lands on the dashboard. The
 * command palette (Ctrl/Cmd+K) is registered app-wide, including inside the room, and the
 * calendar reminder loop (browser notifications before an event starts, vc.notificationPrefs)
 * runs app-wide too: one loop per mount, stopped on unmount, inert while reminders are off,
 * the browser permission is missing or nobody is signed in.
 */
export function App(): JSX.Element {
  const pathname = usePathname();

  // One reminder loop per mount, wired to the real browser: preferences re-read from
  // localStorage each tick, the token from the shared SSO store (the calendar client heals
  // an expired access token via its own 401-refresh-retry), real Notification as the sink.
  // The first tick is gated on prefs.enabled, so the loop stays quiet until the user opts in.
  useEffect(() => {
    const storage = browserStorage();
    if (storage === null) {
      return;
    }
    const stop = startReminderLoop({
      prefs: () => loadNotificationPrefs(storage),
      token: () => {
        const ssoStore = browserSsoStorage();
        if (ssoStore === null) {
          return null;
        }
        const session = loadSsoSession(ssoStore);
        return session === null ? null : session.accessToken;
      },
      fetchImpl: (input, init) => globalThis.fetch(input, init),
      now: () => new Date(),
      notify: (title, timeText) => {
        new Notification(title, {
          body: i18n.t('settings.notif_reminder_body', { time: timeText }),
        });
      },
      permission: () => (typeof Notification === 'undefined' ? 'denied' : Notification.permission),
      storage,
    });
    return stop;
  }, []);

  const slug = roomSlugFromPath(pathname);
  const webinarEventId = parseWebinarDetailPath(pathname);
  let page: JSX.Element;
  if (slug !== null) {
    page = <RoomPage slug={slug} />;
  } else if (pathname === '/') {
    page = <DashboardPage />;
  } else if (pathname === '/welcome') {
    page = <LandingPage />;
  } else if (pathname === '/calendar') {
    page = (
      <Suspense fallback={<div className="min-h-screen bg-background" />}>
        <CalendarPage />
      </Suspense>
    );
  } else if (pathname === '/recordings') {
    page = <RecordingsPage />;
  } else if (pathname === '/webinars') {
    page = <WebinarsPage />;
  } else if (webinarEventId !== null) {
    // CR-27: /webinars/<event-id> - the DevHub-style webinar detail page.
    page = <WebinarDetailPage eventId={webinarEventId} />;
  } else if (pathname === '/analytics') {
    page = <AnalyticsPage />;
  } else if (pathname === '/settings') {
    page = <SettingsPage />;
  } else {
    // /dashboard and every unknown path resolve to the home dashboard; the query (e.g. an
    // in-flight ?sso=callback from an old bookmark) travels along.
    page = <Redirect to="/" search={window.location.search} />;
  }
  return (
    <>
      {page}
      <CommandPalette pathname={pathname} />
    </>
  );
}
